import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, inject, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';

const vertexSource = `#version 300 es
void main() {
  vec2 positions[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  gl_Position = vec4(positions[gl_VertexID], 0.0, 1.0);
}`;

// The Figma fill uses three octaves of noise, domain warp, and a height-mapped gradient.
// This screen-space version keeps those moving features without its 150k-triangle sphere.
const fragmentSource = `#version 300 es
precision highp float;
uniform vec2 uResolution;
uniform float uTime;
out vec4 color;

vec3 hash33(vec3 p) {
  vec3 q = vec3(dot(p, vec3(127.1, 311.7, 74.7)),
                dot(p, vec3(269.5, 183.3, 246.1)),
                dot(p, vec3(113.5, 271.9, 124.6)));
  return fract(sin(q) * 43758.5453) * 2.0 - 1.0;
}

float noise3(vec3 p) {
  vec3 cell = floor(p);
  vec3 local = fract(p);
  vec3 blend = local * local * local * (local * (local * 6.0 - 15.0) + 10.0);
  float a = dot(hash33(cell), local);
  float b = dot(hash33(cell + vec3(1, 0, 0)), local - vec3(1, 0, 0));
  float c = dot(hash33(cell + vec3(0, 1, 0)), local - vec3(0, 1, 0));
  float d = dot(hash33(cell + vec3(1, 1, 0)), local - vec3(1, 1, 0));
  float e = dot(hash33(cell + vec3(0, 0, 1)), local - vec3(0, 0, 1));
  float f = dot(hash33(cell + vec3(1, 0, 1)), local - vec3(1, 0, 1));
  float g = dot(hash33(cell + vec3(0, 1, 1)), local - vec3(0, 1, 1));
  float h = dot(hash33(cell + vec3(1, 1, 1)), local - vec3(1, 1, 1));
  float xy0 = mix(mix(a, b, blend.x), mix(c, d, blend.x), blend.y);
  float xy1 = mix(mix(e, f, blend.x), mix(g, h, blend.x), blend.y);
  return mix(xy0, xy1, blend.z) * 1.1547;
}

float fbm(vec3 p) {
  float total = 0.0;
  float amplitude = 1.0;
  float weight = 0.0;
  for (int i = 0; i < 3; i++) {
    total += noise3(p) * amplitude;
    weight += amplitude;
    p = vec3(0.8 * p.y + 0.6 * p.z,
            -0.8 * p.x + 0.36 * p.y - 0.48 * p.z,
            -0.6 * p.x - 0.48 * p.y + 0.64 * p.z) * 2.02 + vec3(3.7, 1.9, 6.3);
    amplitude *= 0.48;
  }
  return total / weight;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 point = (uv - 0.5) * vec2(0.55, 0.45);
  float angle = uTime * 0.0375; // Figma speed: 15% of 0.25 rad/s.
  point = mat2(cos(angle), sin(angle), -sin(angle), cos(angle)) * point;
  float morphTime = uTime * 3.73;
  vec3 p = vec3(point * 2.97, 0.5) + vec3(1.7, 3.1, 5.3);
  vec3 drift = normalize(vec3(0.73, -0.41, 0.55)) * morphTime * 0.105;
  vec3 warpDrift = normalize(vec3(-0.46, 0.38, 0.80)) * morphTime * 0.137;
  vec3 warp = vec3(noise3(p * 0.55 + warpDrift),
                   noise3(p * 0.55 + warpDrift + vec3(5.2, 1.3, 2.8)),
                   noise3(p * 0.55 + warpDrift + vec3(1.7, 9.2, 4.4))) * 0.26;
  float field = fbm(p + drift + warp);
  float t = smoothstep(0.34, 0.66, field * 1.85 + 0.5);
  // Selected Figma colors (cyan to blue); the canvas is composited at 20% opacity.
  color = vec4(mix(vec3(0.714, 1.0, 1.0), vec3(0.0, 0.114, 1.0), t), 1.0);
}`;

@Directive({ selector: '[appMovingGradient]' })
export class MovingGradientDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private canvas?: HTMLCanvasElement;
  private gl?: WebGL2RenderingContext;
  private program?: WebGLProgram;
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;
  private motionQuery?: MediaQueryList;
  private frameId = 0;
  private visible = true;
  private lastFrame = 0;
  private startTime = 0;
  private readonly onMotionChange = () => this.schedule();
  private readonly onVisibilityChange = () => this.schedule();

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.zone.runOutsideAngular(() => this.initialize());
  }

  private initialize(): void {
    const canvas = document.createElement('canvas');
    canvas.className = 'blue-panel-shader';
    const gl = canvas.getContext('webgl2', { alpha: false, antialias: false });
    if (!gl) return;

    const vertex = this.compile(gl, gl.VERTEX_SHADER, vertexSource);
    const fragment = this.compile(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vertex || !fragment) return;
    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return;
    }

    this.canvas = canvas;
    this.gl = gl;
    this.program = program;
    this.host.prepend(canvas);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.schedule();
    });
    this.intersectionObserver.observe(this.host);
    this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener('change', this.onMotionChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.startTime = performance.now();
    this.resize();
  }

  private compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
    gl.deleteShader(shader);
    return null;
  }

  private resize(): void {
    if (!this.canvas || !this.gl) return;
    this.canvas.width = Math.max(1, Math.round(this.host.clientWidth * 0.8));
    this.canvas.height = Math.max(1, Math.round(this.host.clientHeight * 0.8));
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    this.draw(performance.now());
    this.schedule();
  }

  private schedule(): void {
    cancelAnimationFrame(this.frameId);
    if (this.visible && !document.hidden && !this.motionQuery?.matches) {
      this.frameId = requestAnimationFrame((time) => this.tick(time));
    }
  }

  private tick(time: number): void {
    if (time - this.lastFrame >= 1000 / 24) {
      this.draw(time);
      this.lastFrame = time;
    }
    this.frameId = requestAnimationFrame((next) => this.tick(next));
  }

  private draw(time: number): void {
    const { gl, program, canvas } = this;
    if (!gl || !program || !canvas) return;
    gl.useProgram(program);
    gl.uniform2f(gl.getUniformLocation(program, 'uResolution'), canvas.width, canvas.height);
    gl.uniform1f(gl.getUniformLocation(program, 'uTime'), (time - this.startTime) / 1000);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.host.classList.add('shader-ready');
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    cancelAnimationFrame(this.frameId);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.gl && this.program) this.gl.deleteProgram(this.program);
    this.canvas?.remove();
  }
}
